import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { LOGO_URL } from "@/lib/brand";

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("pt-BR", { dateStyle: "long", timeStyle: "short" });
  } catch { return iso; }
};

const LEVEL_COLOR = {
  Crítico: { bg: "#dc2626", text: "#fff" },
  Alto:    { bg: "#f97316", text: "#fff" },
  Moderado:{ bg: "#f59e0b", text: "#fff" },
  Baixo:   { bg: "#10b981", text: "#fff" },
};

const INSTR_COLOR = {
  rapido: "#10b981",
  copsoq: "#6366f1",
  hse:    "#f59e0b",
};

/* -------- Pure SVG visualizations for the print layout -------- */

function DimensionsRadar({ dims }) {
  // dims comes from aggregate.scoring.dimensions (9 legacy dimensions, score 0..5)
  const valid = (dims || []).filter((d) => d && typeof d.score === "number");
  if (valid.length < 3) return null; // radar needs at least 3 axes
  const size = 360;
  const cx = size / 2;
  const cy = size / 2;
  const rMax = 120;
  const rings = [1, 2, 3, 4, 5];
  const angleFor = (i) => (-90 + (360 / valid.length) * i) * (Math.PI / 180);
  const pt = (i, r) => [cx + r * Math.cos(angleFor(i)), cy + r * Math.sin(angleFor(i))];
  const dataPolygon = valid
    .map((d, i) => pt(i, (Math.max(0, Math.min(5, d.score)) / 5) * rMax))
    .map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`)
    .join(" ");
  const splitLabel = (label, max = 16) => {
    if (!label) return [""];
    if (label.length <= max) return [label];
    const words = label.split(" ");
    const lines = [];
    let cur = "";
    for (const w of words) {
      if ((cur + " " + w).trim().length > max) {
        if (cur) lines.push(cur);
        cur = w;
      } else {
        cur = (cur + " " + w).trim();
      }
    }
    if (cur) lines.push(cur);
    return lines.slice(0, 3); // cap at 3 lines per label
  };
  return (
    <svg viewBox={`0 0 ${size} ${size}`} width="100%" style={{ maxWidth: 360, display: "block", margin: "0 auto" }}>
      {/* grid rings */}
      {rings.map((v) => (
        <circle
          key={v}
          cx={cx}
          cy={cy}
          r={(v / 5) * rMax}
          fill="none"
          stroke="#cbd5e1"
          strokeWidth={v === 5 ? 1.2 : 0.5}
          strokeDasharray={v === 5 ? "0" : "2,2"}
        />
      ))}
      {/* axes */}
      {valid.map((_, i) => {
        const [x2, y2] = pt(i, rMax);
        return <line key={i} x1={cx} y1={cy} x2={x2} y2={y2} stroke="#cbd5e1" strokeWidth={0.6} />;
      })}
      {/* data polygon */}
      <polygon
        points={dataPolygon}
        fill="rgba(4, 120, 87, 0.18)"
        stroke="#047857"
        strokeWidth={1.4}
        strokeLinejoin="round"
      />
      {/* data points */}
      {valid.map((d, i) => {
        const [x, y] = pt(i, (Math.max(0, Math.min(5, d.score)) / 5) * rMax);
        return <circle key={i} cx={x} cy={y} r={2.4} fill="#047857" />;
      })}
      {/* labels */}
      {valid.map((d, i) => {
        const [lx, ly] = pt(i, rMax + 22);
        const ang = angleFor(i);
        const cos = Math.cos(ang);
        const anchor = Math.abs(cos) < 0.2 ? "middle" : cos > 0 ? "start" : "end";
        const lines = splitLabel(d.name);
        return (
          <text
            key={i}
            x={lx}
            y={ly}
            textAnchor={anchor}
            fontSize="7.5"
            fontWeight="600"
            fill="#0f172a"
          >
            {lines.map((ln, k) => (
              <tspan key={k} x={lx} dy={k === 0 ? 0 : 9}>{ln}</tspan>
            ))}
            <tspan x={lx} dy={9} fontSize="7" fontWeight="700" fill="#047857">
              {d.score.toFixed(2)} {d.label !== "Sem dados" ? `· ${d.label}` : ""}
            </tspan>
          </text>
        );
      })}
      {/* scale legend (top-left) */}
      <g transform="translate(8, 12)">
        <text fontSize="7" fontWeight="700" fill="#64748b">Escala 0–5 (5 = mais crítico)</text>
      </g>
    </svg>
  );
}

function InstrumentsBarChart({ byInstrument, totalResponses }) {
  const items = byInstrument || [];
  if (items.length === 0) return null;
  const maxResp = Math.max(1, ...items.map((b) => b.response_count));
  const rowH = 28;
  const labelW = 180;
  const barW = 340;
  const padX = 8;
  const width = labelW + barW + 60 + padX * 2;
  const height = rowH * items.length + 18;
  return (
    <svg viewBox={`0 0 ${width} ${height}`} width="100%" style={{ maxWidth: 540, display: "block", margin: "8px auto 0" }}>
      <text x={padX} y={10} fontSize="8" fontWeight="700" fill="#64748b">
        Respostas por instrumento (n = {totalResponses})
      </text>
      {items.map((b, i) => {
        const y = 18 + i * rowH;
        const w = (b.response_count / maxResp) * barW;
        const color = INSTR_COLOR[b.test_id] || "#64748b";
        return (
          <g key={b.test_id}>
            <text x={padX} y={y + 14} fontSize="9" fontWeight="600" fill="#0f172a">
              {b.test_name}
            </text>
            <rect x={labelW} y={y} width={barW} height={16} fill="#f1f5f9" rx={2} />
            <rect x={labelW} y={y} width={w} height={16} fill={color} rx={2} />
            <text x={labelW + w + 6} y={y + 12} fontSize="8.5" fontWeight="700" fill="#1e293b">
              {b.response_count} resp · {b.campaign_count} campanha{b.campaign_count !== 1 ? "s" : ""}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

export default function CompanyAIAnalysisPDF() {
  const { companyId, aid } = useParams();
  const [data, setData] = useState(null);
  const [aggregate, setAggregate] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([
      api.get(`/psy/companies/${companyId}/ai-analyses/${aid}`),
      api.get(`/psy/companies/${companyId}/aggregate`),
    ])
      .then(([a, r]) => { setData(a.data); setAggregate(r.data); })
      .catch((e) => setError(e.response?.data?.detail || "Erro ao carregar"));
  }, [companyId, aid]);

  useEffect(() => {
    if (data && aggregate) {
      const t = setTimeout(() => window.print(), 800);
      return () => clearTimeout(t);
    }
  }, [data, aggregate]);

  if (error) return <div className="p-10 text-center text-rose-700" data-testid="company-pdf-error">{error}</div>;
  if (!data || !aggregate) return <div className="p-10 text-center text-slate-400" data-testid="company-pdf-loading">Carregando relatório consolidado…</div>;

  const a = data.analysis;
  const company = aggregate.company;
  const scoring = aggregate.scoring;

  return (
    <div className="pdf-root" data-testid="company-pdf-root">
      <style>{`
        @page { size: A4; margin: 18mm 14mm 18mm 14mm; }
        @media print {
          body { background: #fff !important; }
          .pdf-root { font-family: 'Inter', -apple-system, sans-serif; color: #0f172a; }
          .page-break { page-break-before: always; }
          .no-print { display: none !important; }
        }
        .pdf-root { font-family: 'Inter', sans-serif; color: #0f172a; max-width: 760px; margin: 0 auto; padding: 30px; background: #fff; }
        .pdf-h1 { font-size: 28px; font-weight: 800; line-height: 1.15; letter-spacing: -0.02em; }
        .pdf-h2 { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.12em; color: #047857; margin: 22px 0 8px; border-bottom: 2px solid #d1fae5; padding-bottom: 6px; }
        .pdf-h3 { font-size: 13px; font-weight: 700; color: #0f172a; margin: 14px 0 5px; }
        .pdf-p { font-size: 11.5px; line-height: 1.65; color: #1e293b; margin: 8px 0; text-align: justify; }
        .pdf-meta { font-size: 10px; color: #64748b; }
        .pdf-table { width: 100%; font-size: 11px; border-collapse: collapse; margin: 8px 0; }
        .pdf-table th, .pdf-table td { padding: 7px 9px; text-align: left; border-bottom: 1px solid #e2e8f0; }
        .pdf-table th { background: #f1f5f9; font-weight: 700; text-transform: uppercase; font-size: 9.5px; letter-spacing: 0.04em; color: #475569; }
        .badge { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 10px; font-weight: 700; }
        .cover { padding: 70px 0 50px; border-bottom: 4px solid #047857; margin-bottom: 28px; }
        .factor-card { border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px 14px; margin: 6px 0; background: #f8fafc; }
        .rec-card { border-left: 3px solid #047857; background: #ecfdf5; padding: 9px 12px; margin: 7px 0; border-radius: 0 6px 6px 0; }
        .disclaimer { background: #fffbeb; border: 1px solid #fde68a; padding: 10px 12px; border-radius: 6px; font-size: 10.5px; color: #78350f; margin-top: 20px; }
        .footer-bar { margin-top: 28px; padding-top: 12px; border-top: 1px solid #e2e8f0; font-size: 9.5px; color: #94a3b8; display: flex; justify-content: space-between; }
        .toolbar { position: fixed; top: 12px; right: 12px; display: flex; gap: 8px; }
        .toolbar button { background: #047857; color: #fff; border: 0; padding: 8px 14px; font-weight: 600; border-radius: 6px; cursor: pointer; font-size: 12px; }
        .toolbar button.secondary { background: #fff; color: #047857; border: 1px solid #047857; }
        .instr-pill { display: inline-block; padding: 2px 8px; margin: 2px 4px 2px 0; font-size: 9.5px; font-weight: 700; background: #ecfdf5; color: #065f46; border-radius: 4px; text-transform: uppercase; letter-spacing: 0.04em; }
      `}</style>

      <div className="toolbar no-print">
        <button onClick={() => window.print()} data-testid="company-pdf-print"><i className="fa-solid fa-print"></i> Imprimir / Salvar PDF</button>
        <button className="secondary" onClick={() => window.close()}>Fechar</button>
      </div>

      {/* COVER */}
      <section className="cover">
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 30 }}>
          <img src={LOGO_URL} alt="Sollute" style={{ height: 42 }} />
          <div style={{ borderLeft: "2px solid #d1fae5", paddingLeft: 14 }}>
            <div className="pdf-meta" style={{ textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, color: "#047857" }}>
              Sollute Ocupacional · NR-01
            </div>
            <div className="pdf-meta">Análise Psicossocial Consolidada</div>
          </div>
        </div>

        <div style={{ background: "#047857", color: "#fff", padding: "5px 12px", borderRadius: 4, fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", display: "inline-block", textTransform: "uppercase" }}>
          Relatório Executivo Consolidado · IA
        </div>

        <h1 className="pdf-h1" style={{ marginTop: 20 }}>
          {company.razao_social || company.nome_fantasia || "—"}
        </h1>
        <p className="pdf-p" style={{ fontSize: 13, color: "#475569", marginTop: 8 }}>
          Diagnóstico psicossocial consolidado · {aggregate.campaign_count} campanha(s) · {aggregate.by_instrument.length} instrumento(s)
        </p>

        <table style={{ width: "100%", fontSize: 11, marginTop: 28, color: "#1e293b" }}>
          <tbody>
            <tr><td style={{ padding: "5px 0", color: "#64748b", width: "32%" }}>CNPJ</td><td style={{ padding: "5px 0", fontWeight: 600 }}>{company.cnpj || "—"}</td></tr>
            <tr><td style={{ padding: "5px 0", color: "#64748b" }}>Instrumentos aplicados</td>
              <td style={{ padding: "5px 0", fontWeight: 600 }}>
                {aggregate.by_instrument.map((b) => (
                  <span key={b.test_id} className="instr-pill">{b.test_name} ({b.campaign_count})</span>
                ))}
              </td>
            </tr>
            <tr><td style={{ padding: "5px 0", color: "#64748b" }}>Trabalhadores alvo</td><td style={{ padding: "5px 0", fontWeight: 600 }}>{aggregate.expected_workers || "—"}</td></tr>
            <tr><td style={{ padding: "5px 0", color: "#64748b" }}>Respondentes consolidados</td>
              <td style={{ padding: "5px 0", fontWeight: 600 }}>
                {scoring.response_count}
                {aggregate.participacao_pct != null && <span style={{ color: "#64748b", fontWeight: 400 }}> ({aggregate.participacao_pct}% de participação)</span>}
              </td>
            </tr>
            <tr><td style={{ padding: "5px 0", color: "#64748b" }}>Score consolidado</td>
              <td style={{ padding: "5px 0", fontWeight: 700 }}>
                {scoring.overall_score.toFixed(2)}
                <span className="badge" style={{ background: LEVEL_COLOR[scoring.overall.label]?.bg || "#94a3b8", color: "#fff", marginLeft: 8 }}>{scoring.overall.label}</span>
              </td>
            </tr>
            <tr><td style={{ padding: "5px 0", color: "#64748b" }}>Data da análise</td><td style={{ padding: "5px 0", fontWeight: 600 }}>{fmtDate(data.created_at)}</td></tr>
            <tr><td style={{ padding: "5px 0", color: "#64748b" }}>Gerado por</td><td style={{ padding: "5px 0", fontWeight: 600 }}>{data.generated_by} · {data.model || "Claude Sonnet 4.6"}</td></tr>
          </tbody>
        </table>
      </section>

      {/* RESUMO EXECUTIVO */}
      <section>
        <h2 className="pdf-h2">1. Resumo Executivo</h2>
        <p className="pdf-p" style={{ fontSize: 12, fontStyle: "italic", color: "#1e293b", borderLeft: "3px solid #047857", paddingLeft: 12 }}>
          {a.resumo_executivo}
        </p>
      </section>

      {/* PERFIL CONSOLIDADO */}
      <section>
        <h2 className="pdf-h2">2. Perfil consolidado da organização</h2>
        <div className="pdf-p" style={{ whiteSpace: "pre-line" }}>{a.perfil_organizacao}</div>
      </section>

      {/* FATORES CRÍTICOS */}
      {a.fatores_criticos?.length > 0 && (
        <section>
          <h2 className="pdf-h2">3. Principais fatores críticos consolidados</h2>
          {a.fatores_criticos.map((f, i) => (
            <div key={i} className="factor-card">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                <strong style={{ fontSize: 12 }}>{f.dimensao}</strong>
                <span className="badge" style={{ background: LEVEL_COLOR[f.nivel]?.bg || "#94a3b8", color: "#fff" }}>{f.nivel} · {f.media?.toFixed?.(2) ?? f.media}</span>
              </div>
              <div style={{ fontSize: 11, color: "#475569" }}>{f.leitura}</div>
            </div>
          ))}
        </section>
      )}

      {/* RANKING */}
      <section>
        <h2 className="pdf-h2">4. Ranking dos riscos consolidados</h2>
        <p className="pdf-p">{a.ranking_riscos}</p>
      </section>

      {/* VISUALIZAÇÃO GRÁFICA — radar + barras por instrumento (4.5) */}
      <section data-testid="company-pdf-charts">
        <h2 className="pdf-h2">4.5 Visualização gráfica consolidada</h2>
        <p className="pdf-meta" style={{ marginBottom: 4 }}>
          Leitura imediata da postura psicossocial da empresa nas 9 dimensões NR-01 e da
          composição amostral por instrumento aplicado.
        </p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "flex-start", marginTop: 6 }}>
          <div style={{ flex: "1 1 320px", minWidth: 280 }}>
            <h3 className="pdf-h3" style={{ marginTop: 0 }}>Radar das 9 dimensões NR-01</h3>
            <DimensionsRadar dims={scoring.dimensions} />
          </div>
          <div style={{ flex: "1 1 280px", minWidth: 260 }}>
            <h3 className="pdf-h3" style={{ marginTop: 0 }}>Distribuição amostral por instrumento</h3>
            <InstrumentsBarChart
              byInstrument={aggregate.by_instrument}
              totalResponses={scoring.response_count}
            />
          </div>
        </div>
      </section>

      {/* INTERPRETAÇÃO POR DIMENSÃO */}
      <section className="page-break">
        <h2 className="pdf-h2">5. Interpretação por dimensão (9 NR-01)</h2>
        {a.interpretacao_dimensoes?.map((d, i) => (
          <div key={i} style={{ marginBottom: 14 }}>
            <h3 className="pdf-h3">
              {d.dimensao}{" "}
              <span className="badge" style={{ background: LEVEL_COLOR[d.nivel]?.bg || "#94a3b8", color: "#fff", marginLeft: 6 }}>
                {d.nivel} · {d.media?.toFixed?.(2) ?? d.media}
              </span>
            </h3>
            <p className="pdf-p">{d.interpretacao}</p>
          </div>
        ))}
      </section>

      {/* RELAÇÃO ENTRE FATORES */}
      <section>
        <h2 className="pdf-h2">6. Relação sistêmica entre fatores</h2>
        <div className="pdf-p" style={{ whiteSpace: "pre-line" }}>{a.relacao_fatores}</div>
      </section>

      {/* CAUSAS & CONSEQUÊNCIAS */}
      <section>
        <h2 className="pdf-h2">7. Causas organizacionais e consequências potenciais</h2>
        <h3 className="pdf-h3">Causas organizacionais possíveis</h3>
        <ul style={{ fontSize: 11.5, lineHeight: 1.6, marginLeft: 18, color: "#1e293b" }}>
          {a.causas_organizacionais?.map((c, i) => <li key={i}>{c}</li>)}
        </ul>
        <h3 className="pdf-h3" style={{ marginTop: 10 }}>Consequências potenciais</h3>
        <ul style={{ fontSize: 11.5, lineHeight: 1.6, marginLeft: 18, color: "#1e293b" }}>
          {a.consequencias_potenciais?.map((c, i) => <li key={i}>{c}</li>)}
        </ul>
      </section>

      {/* RECOMENDAÇÕES */}
      {a.recomendacoes_prioritarias?.length > 0 && (
        <section className="page-break">
          <h2 className="pdf-h2">8. Recomendações prioritárias para a diretoria</h2>
          {a.recomendacoes_prioritarias.map((r, i) => (
            <div key={i} className="rec-card">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
                <strong style={{ fontSize: 12 }}>
                  {r.ordem || i + 1}. {r.titulo}
                </strong>
                {r.horizonte && <span style={{ fontSize: 9.5, fontWeight: 700, color: "#065f46", textTransform: "uppercase", letterSpacing: "0.04em" }}>{r.horizonte}</span>}
              </div>
              <div style={{ fontSize: 11, color: "#1e293b", marginTop: 3 }}>{r.descricao}</div>
            </div>
          ))}
        </section>
      )}

      {/* CONCLUSÃO */}
      <section>
        <h2 className="pdf-h2">9. Conclusão técnica</h2>
        <div className="pdf-p" style={{ whiteSpace: "pre-line", background: "#f8fafc", padding: 12, borderRadius: 6, border: "1px solid #e2e8f0" }}>
          {a.conclusao_tecnica}
        </div>
      </section>

      {/* CAMPANHAS QUE COMPÕEM */}
      <section>
        <h2 className="pdf-h2">10. Campanhas que compõem esta análise</h2>
        <table className="pdf-table">
          <thead>
            <tr>
              <th>Campanha</th>
              <th>Instrumento</th>
              <th>Setor / GHE</th>
              <th>Respostas</th>
            </tr>
          </thead>
          <tbody>
            {aggregate.campaigns.map((c) => (
              <tr key={c.id}>
                <td style={{ fontWeight: 600 }}>{c.titulo}</td>
                <td>{c.test_name}</td>
                <td>{c.setor_ghe || "—"}</td>
                <td>{c.response_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* DISCLAIMER */}
      <div className="disclaimer">
        <strong>Aviso técnico:</strong> {a.disclaimer}
      </div>

      <footer className="footer-bar">
        <div>Sollute Ocupacional · NR-01 · Documento confidencial</div>
        <div>Análise consolidada gerada por IA · Modelo {data.model}</div>
      </footer>
    </div>
  );
}
